import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useSplitTreatments } from '@splitsoftware/splitio-react'

import { selectCurrentUser } from '@/app/slices/appSlice'
import { selectOrganisation } from '@/app/slices/organisationSlice'
import { getProductDetails, RESELLERS } from '@/common/utils'

const splitTreatments = {
    isReady: false,
    previewResellerName: undefined,
    failedPaymentWallMode: 'ToolBox_FailedPaymentWallMode',
    pauseWallMode: 'ToolBox_PauseWallMode',
    replaceSplinesWithLinearSegments: 'Toolbox_ReplaceSplineWithLinearSegmentsUnfold3d',
    show3dButton: 'ToolBox_Show3DPartsButton',
    showAdminTools: 'ToolBox_ShowAdminTools',
    showChurnkeyCancelSubscription: 'ToolBox_ChurnkeyCancelSubscription',
    showCsvImportExportButtons: 'ToolBox_ImportExportSheetsRateTables',
    showCustomerCentral: 'ToolBox_CustomerCentral',
    showCustomers: 'ToolBox_customers',
    showCustomerTaxRates: 'ToolBox_CustomerTaxRates',
    showCuttingTechManagement: 'ToolBox_ShowCuttingTechManagement',
    showCuttingTechModal: 'ToolBox_CuttingTechSelectionModal',
    showDownloadPDFs: 'ToolBox_DownloadPDFs',
    showEmailCustomerModal: 'ToolBox_EmailCustomerModal',
    showEnableNestView: 'ToolBox_EnableNestView',
    showExport3DModels: 'ToolBox_3dModelFile',
    showFixedPriceParts: 'ToolBox_FixedPriceParts',
    showFolding: 'ToolBox_folding',
    showFoldingProductionWarnings: 'ToolBox_FoldingProductionWarnings',
    showFullTubeLengths: 'ToolBox_FullTubeLengths',
    showGoogleDriveIntegration: 'ToolBox_GoogleDrive',
    showGrainDirection: 'ToolBox_GrainDirection',
    showIntegrations: false,
    showIntegrationStatus: 'ToolBox_QBOXeroSuccessStatus',
    showIntercom: 'ToolBox_Intercom',
    showLibellulaWhitelabelling: 'ToolBox_libellula_whitelabel',
    showMaterialRevamp: 'ToolBox_material_revamp',
    showMiscItems: 'ToolBox_misc_items',
    showMultipleTaxRates: 'ToolBox_MultipleTaxRates',
    showNestView: 'ToolBox_DisplayNestView',
    showOrdering: 'ToolBox_ordering',
    showPartLibrary: 'ToolBox_PartLibrary',
    showPayments: 'ToolBox_Payments',
    showPDFAttachmentCheckbox: 'ToolBox_PDFAttachments',
    showPdfButton: 'ToolBox_PDF',
    showPdfFooterFields: 'ToolBox_PDFFooterFields',
    showPricing: 'ToolBox_quoting_iteration1',
    showProductionPDFs: 'ToolBox_ProductionPDFs',
    showQuickBooks: 'ToolBox_QuickBooks',
    showQuotingIteration2: 'ToolBox_quoting_iteration2',
    showRetryIntegration: 'ToolBox_RetryIntegration',
    showSecondaryProcesses: 'ToolBox_secondary_operations',
    showSecondaryProcessesWebstore: 'ToolBox_SecProcWebstore',
    showSecPropCustomProperties: 'ToolBox_SecProcCustomProperties',
    showShipping: 'ToolBox_Shipping',
    showStrikerWhitelabelling: 'ToolBox_Striker_whitelabel',
    showTube: 'ToolBox_Tube',
    showTubeOptions: 'ToolBox_RelaxTubeConstraints',
    showTubeV2Settings: 'ToolBox_TubeV2Setting',
    showUserflowTooltips: 'ToolBox_TooltipsToggle',
    showViewer3D: 'ToolBox_ShowViewer3D',
    showViewerTube: 'ToolBox_ShowViewerTube',
    showVoucherCode: 'ToolBox_VoucherCode',
    showWebStore: 'ToolBox_WebStore',
    showWebStoreFeatureToggles: 'ToolBox_WebStoreFeatureToggles',
    showWebStoreStandaloneToggle: 'WebStore_StandaloneOptIn',
    showWebStoreTube: 'ToolBox_WebStoreTube',
    showXero: 'ToolBox_Xero',
    showCustomisationsettings: 'Toolbox_customization_settings',
}

const resellerName = import.meta.env.VITE_RESELLER

const RAW_VALUE_TREATMENT_KEYS = new Set(['failedPaymentWallMode'])

const RENAME_CONSUMPTION_MODES_FLAG = 'ToolBox_RenameConsumptionModes'

/**
 * useToolBoxTreatments - Resolves ToolBox/WebStore Harness FME (Split) feature flags
 *
 * Evaluates most flags in one batched call against the default client (keyed by the
 * current user's email). `showRenamedConsumptionModes` (`ToolBox_RenameConsumptionModes`)
 * has traffic type "organisation" in Harness FME, so it is evaluated separately against a
 * client keyed by `organisationId`, with `orgCreatedAt` passed as an epoch-ms attribute.
 *
 * @returns {Object} Map of treatment keys to booleans (or raw string for wall-mode keys),
 *   plus `isReady` (true once both clients are ready and a current user exists) and
 *   `previewResellerName`.
 */
export function useToolBoxTreatments() {
    const currentUser = useSelector(selectCurrentUser)
    const organisation = useSelector(selectOrganisation)

    const productDetails = getProductDetails()

    const attributes = {
        emailAddress: currentUser ? currentUser.emailAddress : undefined,
        reseller: resellerName ? RESELLERS[resellerName].name : undefined,
        organisationId: organisation?.organisationId,
        paymentGatewayCustomerId: organisation?.paymentGatewayCustomerId,
    }

    const cleanSplitTreatments = { ...splitTreatments }
    delete cleanSplitTreatments.isReady
    delete cleanSplitTreatments.previewResellerName
    delete cleanSplitTreatments.showIntegrations

    const { isReady, treatments } = useSplitTreatments({ names: Object.values(cleanSplitTreatments), attributes })

    // ToolBox_RenameConsumptionModes has traffic type "organisation" in Harness FME and targets
    // on the organisation key + orgCreatedAt, so it must be evaluated against a client keyed by
    // organisationId rather than the default (user-email-keyed) client used above.
    const { isReady: orgIsReady, treatments: orgTreatments } = useSplitTreatments({
        names: [RENAME_CONSUMPTION_MODES_FLAG],
        splitKey: organisation?.organisationId ?? 'anonymous',
        attributes: {
            orgCreatedAt: organisation?.createDateUtc ? new Date(organisation.createDateUtc).getTime() : undefined,
        },
    })

    const resultTreatments = useMemo(() => {
        const result = {
            ...splitTreatments,
        }
        const baseTreatments = Object.keys(splitTreatments).reduce((acc, key) => {
            const treatmentValue = treatments[splitTreatments[key]]?.treatment
            acc[key] = RAW_VALUE_TREATMENT_KEYS.has(key) ? (treatmentValue ?? 'control') : treatmentValue === 'on'
            return acc
        }, result)

        // Apply overrides and computed values
        baseTreatments.showLibellulaWhitelabelling =
            baseTreatments.showLibellulaWhitelabelling || import.meta.env.VITE_RESELLER === RESELLERS.libellula.name
        baseTreatments.showStrikerWhitelabelling =
            baseTreatments.showStrikerWhitelabelling || import.meta.env.VITE_RESELLER === RESELLERS.striker.name
        baseTreatments.previewResellerName = baseTreatments.showLibellulaWhitelabelling
            ? RESELLERS.libellula.name
            : baseTreatments.showStrikerWhitelabelling
              ? RESELLERS.striker.name
              : undefined
        baseTreatments.showWebStore = baseTreatments.showWebStore && productDetails.hasWebStoreAccess
        baseTreatments.showIntegrations = baseTreatments.showXero || baseTreatments.showQuickBooks
        baseTreatments.showRenamedConsumptionModes = orgTreatments[RENAME_CONSUMPTION_MODES_FLAG]?.treatment === 'on'
        baseTreatments.isReady = isReady && orgIsReady && !!currentUser

        return baseTreatments
    }, [treatments, orgTreatments, isReady, orgIsReady, productDetails, currentUser])

    return resultTreatments
}
